// The footer (SPEC-redesign §5.1, §5.7): the „Warstwy" button with its counter
// over the four sources we have, and **deviations only** — nothing that is in
// its default state ever appears here. Each chip clears exactly its own
// parameter, which is the second way out of a mute the help screen promises and
// the only way out of one whose rows are scrolled off the top (§9.5).
import type { Voice } from '../shared/model.ts';
import { viewState, type ViewState } from '../state/viewstate.ts';
import { countOn, LAYERS } from './layers.ts';

/** The tag a voice wears in a row, and therefore in its chip. */
const VOICE_TAG: Record<Voice, string> = {
  ztm: 'ZTM',
  ztmk: 'ZTM·K',
  pl: 'PL',
  aq: 'AQ',
};

export type Chip = { label: string; clear: Partial<ViewState> };

/**
 * What is not the default, in the order the footer shows it: silences first,
 * then solo, then the line lens. A view with nothing switched off has no chips
 * at all, which is the point — the footer is a report of deviations, not a
 * control panel (§5.7).
 */
export function chips(view: ViewState): Chip[] {
  const out: Chip[] = [];
  for (const { voice } of LAYERS) {
    if (!view.muted.includes(voice)) continue;
    out.push({
      label: `cisza: ${VOICE_TAG[voice]}`,
      clear: { muted: view.muted.filter((v) => v !== voice) },
    });
  }
  if (view.solo) out.push({ label: `tylko: ${VOICE_TAG[view.solo]}`, clear: { solo: null } });
  if (view.onlyLine) out.push({ label: `tylko: linia ${view.onlyLine}`, clear: { onlyLine: null } });
  return out;
}

/**
 * Three stacked squares, read from the prototype's own SVG rather than sketched:
 * `18x18`, rects at (1,1), (4,4) and (7,7), each `10x10` with a `1.5` stroke.
 * The two in front are filled with the paper so the one behind is occluded —
 * that fill is what makes them read as a stack instead of a lattice.
 *
 * Decoration: the button is named, so this is `aria-hidden`. The colour comes
 * from `currentColor`, never from a literal (fitness function 2).
 */
function layersGlyph(): SVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 18 18');
  svg.setAttribute('width', '18');
  svg.setAttribute('height', '18');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  for (const [x, y] of [
    [1, 1],
    [4, 4],
    [7, 7],
  ]) {
    const rect = document.createElementNS(ns, 'rect');
    rect.setAttribute('x', String(x));
    rect.setAttribute('y', String(y));
    rect.setAttribute('width', '10');
    rect.setAttribute('height', '10');
    rect.setAttribute('fill', x === 1 ? 'none' : 'var(--bg)');
    rect.setAttribute('stroke', 'currentColor');
    rect.setAttribute('stroke-width', '1.5');
    svg.append(rect);
  }
  return svg;
}

/**
 * A chip outlives any one render, so it works out what to clear **when it is
 * pressed** rather than carrying a patch captured when it was built: `cisza:
 * ZTM` has to remove ZTM from whatever `cisza` holds now, not from what it held
 * three renders ago.
 */
function chipEl(label: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'footer__chip';
  b.dataset.chip = label;
  // The trailing ✕ is part of the name, and it is what tells a chip from the
  // button that opens a screen of the same name (`e2e/po/screens.ts`).
  b.textContent = `${label} ✕`;
  b.addEventListener('click', () => {
    const chip = chips(viewState.get()).find((c) => c.label === label);
    // Clearing a deviation is not an opening, and it has to survive a step back
    // the same way the deviation itself did (D-b).
    if (chip) viewState.set(chip.clear, 'replace');
  });
  return b;
}

function layersButton(): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'footer__layers';
  b.setAttribute('aria-label', 'Warstwy');
  b.append(layersGlyph());
  const counter = document.createElement('span');
  counter.className = 'footer__counter';
  b.append(counter);
  b.addEventListener('click', () => {
    viewState.set({ screen: 'warstwy' }, 'open');
  });
  return b;
}

/**
 * Paints the footer **in place**. It is repainted on every snapshot — every ten
 * seconds — and replacing its children would take a resident's focus off a chip
 * they were about to press, on a clock. Nothing here reads the snapshot at all:
 * a view that has not changed leaves the DOM untouched, which is the property
 * the E2E asserts by counting `childList` mutations across a poll.
 */
export function paintFooter(host: HTMLElement, view: ViewState): void {
  let layers = host.querySelector<HTMLButtonElement>('.footer__layers');
  let strip = host.querySelector<HTMLElement>('.footer__chips');
  if (!layers || !strip) {
    layers = layersButton();
    strip = document.createElement('div');
    // 52 px is all the height there is, so a resident with four deviations
    // scrolls sideways rather than losing the fourth one off the edge (§5.1).
    strip.className = 'footer__chips';
    host.replaceChildren(layers, strip);
  }

  const counter = layers.querySelector('.footer__counter');
  // `3 / 4`: the prototype's own text is `4/9`, but its three parts are flex
  // items separated by the button's 8 px gap, so it reads spaced. Ours is one
  // string and carries the spacing itself (`white-space: pre` keeps it).
  const reading = `${countOn(view)} / ${LAYERS.length}`;
  // assigning the same string would still be a character-data mutation
  if (counter && counter.textContent !== reading) counter.textContent = reading;

  const wanted = chips(view);
  const have = new Map<string, HTMLElement>();
  for (const el of strip.children) {
    if (el instanceof HTMLElement && el.dataset.chip) have.set(el.dataset.chip, el);
  }
  for (const [label, el] of have) {
    if (!wanted.some((c) => c.label === label)) el.remove();
  }
  wanted.forEach((chip, i) => {
    const el = have.get(chip.label) ?? chipEl(chip.label);
    // only when it is not already exactly there: moving a node it is standing
    // on would blur it just as surely as replacing it
    if (strip.children[i] !== el) strip.insertBefore(el, strip.children[i] ?? null);
  });
}
